import { v4 as uuidv4 } from "uuid";
import {
  AssociatedDocumentSharingInput,
  Recipient,
  SharingMode,
} from "../../../../../../../../../__generated__/graphql";
import type { ActivityFormValues } from "../zod/activitySchema";
import type { UseFormSetError } from "react-hook-form";
import { formatReminderToIsoDate, type Reminder } from "../utils/reminderUtils";
import type { DocumentMetadata } from "../types";
import type { ContactMetadata } from "../components/ContactsSection";
import type { ActivityFragment } from "../../../../../../../../../__generated__/graphql";

interface SubmitParams {
  displayEndTime: boolean;
  displayDescription: boolean;
  displayLocation: boolean;
  displayDocuments: boolean;
  displayContacts: boolean;
  documents: DocumentMetadata[];
  reminders: Reminder[];
  contacts: ContactMetadata[];
  defaultActivity?: ActivityFragment | null;
  isDuplicate: boolean;
  uploadDocuments: (variables: { bucketKey: string }) => Promise<{
    data?: { signedUrlToUploadFile?: string | null } | null;
  }>;
  validateDocuments: (documents: DocumentMetadata[]) => boolean;
}

export function validateEndDateTime(
  data: ActivityFormValues,
  displayEndTime: boolean,
  setError: UseFormSetError<ActivityFormValues>
): boolean {
  if (!displayEndTime) return true;

  if (!data.endDay) {
    setError("endDay", {
      message: "La date de fin est requise",
    });
    return false;
  }

  if (!data.endTime) {
    setError("endTime", {
      message: "L'heure de fin est requise",
    });
    return false;
  }

  if (data.endDay < data.startDay) {
    setError("endDay", {
      message: "La date de fin ne peut pas être avant la date de début",
    });
    return false;
  }

  if (data.endDay === data.startDay && data.endTime <= data.startTime) {
    setError("endTime", {
      message: "L'heure de fin ne peut pas être avant l'heure de début",
    });
    return false;
  }

  return true;
}

export async function buildActivityPayload(
  data: ActivityFormValues,
  params: SubmitParams
) {
  const {
    displayEndTime,
    displayDescription,
    displayLocation,
    displayDocuments,
    displayContacts,
    documents,
    reminders,
    contacts,
    defaultActivity,
    isDuplicate,
    uploadDocuments,
    validateDocuments,
  } = params;

  let uploadedDocuments:
    | {
      id?: string;
      title: string;
      description: string;
      bucketKey: string;
      isPublic: boolean;
      mimeType: string;
      documentSharings?: AssociatedDocumentSharingInput[];
    }[]
    | null = null;

  // Validate and upload documents if they are displayed
  if (displayDocuments) {
    const alreadyUploadedDocuments = documents
      .filter((doc) => doc.file === null && doc.bucketKey !== null)
      .map((doc) => ({
        id: !isDuplicate ? doc.id : undefined,
        title: doc.title,
        description: doc.description,
        bucketKey: doc.bucketKey as string,
        isPublic: doc.isPublic,
        mimeType: doc.mimeType,
        documentSharings: doc.selectedParticipants.map((p) => ({
          recipientUserOrganizationId: p.userOrganizationId,
          recipientOrganizationId: p.organizationId,
          sharingMode: SharingMode.Shared,
        })),
      }));

    const documentsToUpload = documents.filter((doc) => doc.file !== null);
    const documentsValid = validateDocuments(documentsToUpload);

    if (!documentsValid) {
      throw new Error("Documents validation failed");
    }

    // Get signed url for each document
    uploadedDocuments = await Promise.all(
      documentsToUpload.map(async (doc) => {
        const uuid = uuidv4();
        const { data: uploadData } = await uploadDocuments({
          bucketKey: uuid,
        });

        if (!uploadData?.signedUrlToUploadFile) {
          throw new Error("Failed to get signed url");
        }

        const response = await fetch(uploadData.signedUrlToUploadFile, {
          method: "PUT",
          body: doc.file,
        });

        if (!response.ok) {
          throw new Error("Failed to upload document");
        }

        return {
          title: doc.title,
          description: doc.description,
          bucketKey: uuid,
          isPublic: doc.isPublic,
          mimeType: doc.mimeType,
          documentSharings: doc.selectedParticipants.map((p) => ({
            recipientUserOrganizationId: p.userOrganizationId,
            recipientOrganizationId: p.organizationId,
            sharingMode: SharingMode.Shared,
          })),
        };
      })
    );

    uploadedDocuments = [...uploadedDocuments, ...alreadyUploadedDocuments];
  }

  const participants: Recipient[] =
    data.participants
      ?.map((participant) => ({
        userOrganizationId: participant.userOrganizationId,
        organizationId: participant.organizationId,
      }))
      .filter((participant) => participant !== undefined) || [];

  const groupRecipients =
    data.groupParticipants?.map((group) => ({
      userGroupId: group.id,
    })) || [];

  const startDate = new Date(`${data.startDay}T${data.startTime}`);
  const endDate = displayEndTime
    ? new Date(`${data.endDay}T${data.endTime}`)
    : null;

  const activityRemindersToIsoDate = reminders.map((r) => {
    return formatReminderToIsoDate(r.number, r.scale, startDate);
  });

  // Prepare contacts for submission
  const contactsToSubmit = displayContacts
    ? contacts
      .filter((c) => c.name.trim() !== "") // Only include contacts with a name
      .map((c) => ({
        name: c.name,
        email: c.email || undefined,
        phoneNumber: c.phoneNumber || undefined,
        description: c.description || undefined,
      }))
    : [];

  // For update: send all contacts - backend will update existing ones, create new ones, and remove missing ones
  const updateContacts = displayContacts
    ? contacts
      .filter((c) => c.name.trim() !== "")
      .map((c) => {
        return {
          id: c.id,
          name: c.name,
          email: c.email || undefined,
          phoneNumber: c.phoneNumber || undefined,
          description: c.description || undefined,
        };
      })
      .filter((c) => c !== null)
    : [];

  const basePayload = {
    title: data.title,
    color: data.color,
    icon: data.icon,
    recipients: participants,
    groupRecipients: groupRecipients.length > 0 ? groupRecipients : undefined,
    startDate: startDate.toISOString(),
    endDate: endDate ? endDate.toISOString() : null,
    description: displayDescription ? data.description : null,
    locationAddress: displayLocation ? data.locationAddress : null,
    locationCity: displayLocation ? data.locationCity : null,
    locationCountryCode: displayLocation ? data.locationCountryCode : null,
    locationLabel: displayLocation ? data.locationLabel : null,
    locationZipCode: displayLocation ? data.locationZipCode : null,
    locationLatitude: displayLocation ? data.locationLatitude : null,
    locationLongitude: displayLocation ? data.locationLongitude : null,
    locationPhotoUrls: displayLocation ? data.locationPhotoUrls : null,
    documents: uploadedDocuments,
    activityReminders: activityRemindersToIsoDate.map((r) => ({
      reminderDate: r,
    })),
  };

  if (defaultActivity && !isDuplicate) {
    return {
      id: defaultActivity.id,
      ...basePayload,
      contacts: updateContacts,
    };
  } else {
    return {
      ...basePayload,
      contacts: contactsToSubmit,
    };
  }
}
